import {
  Button,
  CommandMenu,
  ModeToggle,
  Toaster,
  useToast,
} from "@bleu.builders/ui";
import { Newspaper, ScrollText, SquarePen } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import React from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import { Link } from "#/components/Link";
import { Notifications } from "#/components/Notifications";
import { Feature, useFeatures } from "#/contexts/FeaturesContext";
import { useImpersonation } from "#/contexts/ImpersonationContext";
import { useOrganization } from "#/contexts/OrganizationContext";
import { useRailsFlash } from "#/contexts/RailsFlashContext";
import { useUser } from "#/contexts/UserContext";
import {
  ADMIN_PAGES,
  AdminPagePath,
  END_USER_PAGES,
  replaceParams,
  UserPagePath,
} from "#/lib/pagesHelper";
import { cn } from "#/lib/utils";
import { client } from "#/utils/api-client";

import { ActiveCheckoutLinkBanner } from "./(components)/ActiveCheckoutLinkBanner";
import { ImpersonationBanner } from "./(components)/ImpersonationBanner";
import { MainNav } from "./(components)/MainNav";
import { MissingBillingInfoBanner } from "./(components)/MissingBillingInfoBanner";
import { MobileDialog } from "./(components)/MobileDialog";
import { PreviewBanner } from "./(components)/PreviewBanner";
import { SidebarNav } from "./(components)/SidebarNav";
import { SubscriptionAlertsBanner } from "./(components)/SubscriptionAlertsBanner";
import { UserNav } from "./(components)/UserNav";

const endUserCommands = ({
  params,
  features,
}: {
  params: Record<string, string>;
  features: Record<Feature, boolean>;
}) =>
  [
    {
      title: "Listar Notícias",
      href: replaceParams(UserPagePath.NEWS_ARTICLES, params),
    },
    {
      title: "Listar Diagnósticos",
      href: replaceParams(UserPagePath.DIAGNOSES, params),
    },
    {
      title: "Criar novo diagnóstico",
      href: replaceParams(UserPagePath.DIAGNOSES_NEW, params),
    },
    {
      title: "Listar Teses",
      href: replaceParams(UserPagePath.THESES, params),
    },
    features.show_petition_models
      ? {
          title: "Listar Petições",
          href: replaceParams(UserPagePath.PETITIONS, params),
        }
      : false,
  ].filter(Boolean) as { title: string; href: string }[];

const adminCommands = ({ params }) => [
  {
    title: "Listar Teses",
    href: replaceParams(AdminPagePath.THESES, params),
  },
  {
    title: "Criar nova Tese",
    href: replaceParams(AdminPagePath.THESES_NEW, params),
  },
  {
    title: "Listar Petições",
    href: replaceParams(AdminPagePath.PETITION_MODELS, params),
  },
  {
    title: "Criar nova Petição",
    href: replaceParams(AdminPagePath.PETITION_MODELS_NEW, params),
  },
  {
    title: "Listar Notícias",
    href: replaceParams(AdminPagePath.NEWS_ARTICLES, params),
  },
  {
    title: "Criar nova Notícia",
    href: replaceParams(AdminPagePath.NEWS_ARTICLES_NEW, params),
  },
  {
    title: "Listar Usuários",
    href: replaceParams(AdminPagePath.USERS, params),
  },
  {
    title: "Listar Assinaturas",
    href: replaceParams(AdminPagePath.SUBSCRIPTIONS, params),
  },
  {
    title: "Listar Métodos de Pagamento",
    href: replaceParams(AdminPagePath.PAYMENT_METHODS, params),
  },
  {
    title: "Listar Cobranças",
    href: replaceParams(AdminPagePath.CHARGES, params),
  },
  {
    title: "Listar Cupons de Desconto",
    href: replaceParams(AdminPagePath.BILLING_COUPONS, params),
  },
  {
    title: "Criar novo Cupom de Desconto",
    href: replaceParams(AdminPagePath.BILLING_COUPONS_NEW, params),
  },
  {
    title: "Listar Planos de Cobrança",
    href: replaceParams(AdminPagePath.BILLING_PLANS, params),
  },
];

const ADMIN_SIDEBAR_ITEMS = [
  {
    title: "Painel de administração",
    items: ADMIN_PAGES,
  },
];

const END_USER_SIDEBAR_ITEMS = [
  {
    title: "Meus dados",
    items: END_USER_PAGES,
  },
];

export function useFlashMessages() {
  const flashMessages = useRailsFlash();
  const { toast } = useToast();

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      flashMessages?.forEach((flash) => {
        const title = flash.type === "alert" ? "Erro" : "Sucesso";

        toast({
          title,
          description: flash.message,
          variant: flash.type === "alert" ? "destructive" : "default",
        });
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, []);
}

const matchesAnyPattern = (pathname, patterns) => {
  return patterns.some((pattern) => pattern.test(pathname));
};

export default function Layout({
  showSidebar = false,
  adminOnly = false,
}: {
  showSidebar?: boolean;
  adminOnly?: boolean;
}) {
  useFlashMessages();

  const organization = useOrganization();
  const { user } = useUser();
  const params = useParams();
  const location = useLocation();
  const posthog = usePostHog();
  const { trueUser } = useImpersonation();
  const { features } = useFeatures();
  const isMobile = window.innerWidth <= 768;

  React.useEffect(() => {
    posthog.capture("$pageview");
  }, [location]);

  React.useEffect(() => {
    if (!trueUser?.id) return;

    posthog.identify(trueUser.email, {
      email: trueUser.email,
      id: trueUser.id,
      is_admin: trueUser.is_admin,
      first_name: trueUser.first_name,
      last_name: trueUser.last_name,
      organization: {
        id: trueUser?.organization?.id,
        name: trueUser?.organization?.name,
        email: trueUser?.organization?.email,
      },
    });
  }, [trueUser]);

  const onAdminPage = location.pathname.startsWith("/admin");

  const sidebarItems = adminOnly ? ADMIN_SIDEBAR_ITEMS : END_USER_SIDEBAR_ITEMS;

  if (!user?.id) {
    return (
      <Navigate
        to={`/login?redirectTo=${location.pathname}${location.search}`}
      />
    );
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to={`/`} />;
  }

  if (
    !user.is_admin &&
    !trueUser?.is_admin &&
    !user.has_completed_onboarding &&
    !location.pathname.endsWith("/onboarding")
  ) {
    return <Navigate to={`/users/${user.id}/onboarding`} />;
  }

  function TopNav() {
    const path = useLocation().pathname;

    const hideNav = path === "/"; // Hide nav on the home page

    if (hideNav) {
      return null;
    }

    return (
      <div className="flex h-12 items-center justify-between w-full">
        <MainNav className="w-full" organization={organization} />
        <div className="flex items-center space-x-4">
          <CommandMenu
            fetcher={async (query) => await client(`/global_search?q=${query}`)}
            icons={{
              NewsArticle: Newspaper,
              Thesis: ScrollText,
              PetitionModel: SquarePen,
            }}
            commands={
              onAdminPage
                ? adminCommands({ params })
                : endUserCommands({ params, features })
            }
          />
        </div>
      </div>
    );
  }

  const bgMutedPages = [/news_articles\/\d+/, /events\/\d+/];

  return (
    <>
      <Toaster position="top-right" />
      <ImpersonationBanner />
      <PreviewBanner />
      <SubscriptionAlertsBanner />
      <MissingBillingInfoBanner />
      <ActiveCheckoutLinkBanner />
      {isMobile && <MobileDialog />}
      <div className="flex-col md:flex bg-background text-foreground h-screen m-auto">
        <div className="border-b">
          <div className="flex py-2 items-center flex-col mx-auto px-6 2xl:px-0 max-w-7xl">
            <div className="flex h-12 items-center justify-between w-full">
              <Link to="/">
                <img
                  className="h-12 -pl-2 -ml-2 dark:hidden"
                  src="https://clickfiscal.com.br/wp-content/uploads/2022/08/cropped-logo-1.webp"
                />
                <img
                  className="hidden dark:block h-16 -pl-4 -ml-4 "
                  src="/images/logo-dark.png"
                  alt=""
                />
              </Link>

              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  className={"max-w-[200px] justify-between"}
                >
                  <p className="truncate">{organization.email}</p>
                </Button>
                {features.show_dark_mode_toggle && <ModeToggle />}
                <Notifications />
                <UserNav />
              </div>
            </div>

            <TopNav />
          </div>
        </div>
        {showSidebar ? (
          <div className="flex-1 space-y-4 pt-0">
            <WithSidebarLayout items={sidebarItems} />
          </div>
        ) : (
          <div
            className={cn("w-full", {
              "bg-muted": matchesAnyPattern(location.pathname, bgMutedPages),
            })}
          >
            <div className="mx-auto px-6 2xl:px-0 max-w-7xl py-10">
              <Outlet />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export const WithSidebarLayout = ({ items }) => {
  return (
    <div className="container w-full flex-1 items-start md:grid md:grid-cols-[220px_minmax(0,1fr)] md:gap-6 lg:grid-cols-[190px_minmax(0,1fr)] lg:gap-10">
      <aside className="z-30 -ml-2 shrink-0 md:sticky h-[calc(100vh-4rem)]">
        {/* 100vh - header height */}
        <SidebarNav items={items} />
      </aside>
      <div className="mb-8 size-full flex-1 flex-col space-y-8 py-0 md:flex">
        <Outlet />
      </div>
    </div>
  );
};
